/**
 * Sirv CDN URL helper: Rewrites product image URLs through Sirv for on-the-fly
 * resize + WebP/AVIF delivery, using Sirv Auto-Fetch.
 *
 * Sirv dynamic-imaging and auto-fetch docs: 
 * https://sirv.com/help/articles/dynamic-imaging/
 * https://sirv.com/help/articles/fetch-images/
 *
 * Example (single URL):
 *   buildSirvUrl('https://apps.kubotausa.com/configurator/img/products/abc.png', { width: 600 })
 *   → 'https://kubota.sirv.com/configurator/img/products/abc.png?w=600&q=82&format=optimal'
 *
 * Example (srcset string for responsive <img srcSet=...>):
 *   buildSirvUrl(url, { widths: [300, 600, 1200] })
 *   → '<sirv-url>?w=300&... 300w, <sirv-url>?w=600&... 600w, <sirv-url>?w=1200&... 1200w'
 */

const SIRV_CDN_BASE_DOMAIN = process.env.REACT_APP_SIRV_BASE_URL;

// Sirv Auto-Fetch is configured for a single prod origin. Staging Calance
// (change.apps.kubotausa.com) is excluded — Sirv would resolve it to prod images.
const ALLOWED_ORIGINS = ['apps.kubotausa.com'];

export function buildSirvUrl(originalUrl, { width, widths, height, quality = 82, format = 'optimal' } = {}) {
    if (!originalUrl) return originalUrl;

    let parsed;
    try { parsed = new URL(originalUrl, window.location.origin); } catch { return originalUrl; }

    if (parsed.hostname.endsWith('.sirv.com')) return originalUrl; // Already a Sirv URL; don't double-rewrite
    if (!ALLOWED_ORIGINS.includes(parsed.hostname)) return originalUrl; // Host not on the allowlist; pass through unchanged

    const build = (w) => {
        const sirvUrl = new URL(parsed.pathname, SIRV_CDN_BASE_DOMAIN);
        if (w) sirvUrl.searchParams.set('w', String(w));
        if (height) sirvUrl.searchParams.set('h', String(height));
        sirvUrl.searchParams.set('q', String(quality));
        sirvUrl.searchParams.set('format', format);
        return sirvUrl.toString();
    };

    if (widths?.length) return widths.map(w => `${build(w)} ${w}w`).join(', ');

    return build(width);
}
